/* eslint-disable @typescript-eslint/no-explicit-any */
import { types, Instance, flow } from 'mobx-state-tree';
import {
  UserEditRequestModel,
  IUserEditRequestModel,
  UserChangeModel,
  IUserChangeModel,
  IUserChangeFormDetailModel,
  UserChangeFormDetailModel,
  INewEditUserRequest,
  NewEditUserRequestModel,
} from './models/userEditRequest.model';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { toFormatDateTime } from 'utils';

const { model, array, string, number } = types;

export const UserEditRequestTableStore = model('UserEditRequestTableStore', {
  userData: array(UserEditRequestModel),
  newUserRequestData: array(NewEditUserRequestModel),
  userComparison: array(UserChangeModel),
  userFormDetail: array(UserChangeFormDetailModel),
  filter: string,
  selectedId: number,
  editselectArray: array(number),
})
  .views((self) => ({
    get userEditRequestView(): IUserEditRequestModel[] {
      const myData = [...self.userData];
      if (!self.filter) {
        return myData;
      }

      const regexp = new RegExp(self.filter, 'i');

      return myData.filter(({ id, email, fullName, description, date, address }) =>
        regexp.test(`${id}${email}${fullName}${description}${date}${address}${toFormatDateTime(date)}`)
      );
    },
    get newuserEditRequestView(): INewEditUserRequest[] {
      const newData = [...self.newUserRequestData];
      if (!self.filter) {
        return newData;
      }
      const regexp = new RegExp(self.filter, 'i');
      return newData.filter(({ id, email, fullName, status, phone, createdAt }) =>
        regexp.test(`${id}${email}${fullName}${status}${phone}${createdAt}${toFormatDateTime(createdAt)}`)
      );
    },
    get userChangeRequestView(): IUserChangeModel[] {
      if (!self.filter) {
        return self.userComparison;
      }

      const regexp = new RegExp(self.filter, 'i');
      return self.userComparison.filter(({ id, field, oldValue, newValue, status }) =>
        regexp.test(`${id}${field}${oldValue}${newValue}${status}`)
      );
    },
    get userChangeFormDetail(): IUserChangeFormDetailModel[] {
      if (!self.filter) {
        return self.userFormDetail;
      }

      const regexp = new RegExp(self.filter, 'i');
      return self.userFormDetail.filter(({ userId, firstName, lastName, address, phone }) =>
        regexp.test(`${userId}${firstName}${lastName}${address}`)
      );
    },
    get selectedUserEditRequest(): IUserEditRequestModel | undefined {
      return self.userData.find(({ id }) => id === self.selectedId);
    },
    get getIds(): number[] {
      const data = [...self.editselectArray];
      return data;
    },
  }))
  .volatile(() => ({}))
  .actions((self) => ({
    normalizeUserEditRequest({ data }: AxiosResponse): any {
      // Transform grouped data structure into flat array of user requests
      const userRequests: any[] = [];

      if (data.data && typeof data.data === 'object') {
        Object.keys(data.data).forEach((userId) => {
          const changes = data.data[userId];
          if (Array.isArray(changes) && changes.length > 0) {
            const firstChange = changes[0];
            const user = firstChange.user;

            userRequests.push({
              id: parseInt(userId),
              email: user.email,
              fullName: `${user.name || ''} ${user.middleName || ''}`.trim(),
              status: changes.some((c: any) => c.status === 'pending') ? 'pending' : changes[0].status,
              description: '',
              date: changes[0].created_at,
              address: null,
              read: false,
              displayName: user.middleName || null,
            });
          }
        });
      }

      self.userData.replace(userRequests);
      return self.userData;
    },
    normalizeNewUserEditRequest({ data }: AxiosResponse): any {
      const dataArray = Array.isArray(data.data) ? data.data : [];
      const normalizedData = dataArray.map((item: any) => ({
        id: item.id,
        email: item.email,
        fullName: `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(),
        uid: item.uid,
        phone: item.phone,
        department: item.department,
        group: item.group,
        status: item.status,
        description: item.description,
        createdAt: item.created_at,
        read: false,
        displayName: item.middleName || null,
      }));
      self.newUserRequestData.replace(normalizedData);
      return self.newUserRequestData;
    },
    normalizeUserChangeRequest({ data }: AxiosResponse): IUserChangeModel[] {
      // Flatten the object structure {"51": [...], "53": [...]} into a single array
      const flattenedData = Object.values(data.data).flat();
      self.userComparison.replace(flattenedData as any);
      return self.userComparison;
    },
    normalizeNewUserRequestRegistration({ data }: AxiosResponse): IUserChangeModel[] {
      try {
        if (!data || !data.data) {
          console.error('No data in response');
          self.userComparison.replace([]);
          return self.userComparison;
        }

        // data.data is an object with details array, not an array itself
        const details = data.data.details || [];
        self.userComparison.replace(details);
        return self.userComparison;
      } catch (error) {
        console.error('Error in getnewUserRequestRegistration:', error);
        self.userComparison.replace([]);
        return self.userComparison;
      }
    },
    normalizeUserFormDetailRequest({ data }: AxiosResponse): IUserChangeFormDetailModel[] {
      self.userFormDetail = data.data;
      return self.userFormDetail;
    },
    setFilter(filter: string) {
      self.filter = filter;
    },
    clearStore(): IUserEditRequestModel[] {
      self.userData.clear();
      self.selectedId = 0;
      return self.userData;
    },
    setSelectedId(id: number): void {
      self.selectedId = id;
    },
    clearSelected(): number {
      self.selectedId = 0;
      return self.selectedId;
    },

    pushId(selectedId: number): void {
      self.editselectArray.push(selectedId);
    },
  }))
  .actions((self) => ({
    getUserEditRequest: flow(function* () {
      const result = yield axios
        .get<IUserEditRequestModel[]>('profile-requests')
        .then(self.normalizeUserEditRequest)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getNewUserEditRequest: flow(function* () {
      const result = yield axios
        .get<INewEditUserRequest[]>('registration-requests')
        .then(self.normalizeNewUserEditRequest)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),

    getEndUserRequest: flow(function* () {
      try {
        const response = yield axios.get('user/pending-requests');
        return response;
      } catch (error: any) {
        throw new Error(error.response?.data?.data?.message || error.message || 'Failed to connect');
      }
    }),

    getUserChange: flow(function* (id: number) {
      const result = yield axios
        .get<IUserChangeModel[]>(`profile-requests/${id}`)
        .then(self.normalizeUserChangeRequest)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getnewUserRequestRegistration: flow(function* (id: number) {
      const result = yield axios
        .get<IUserChangeModel[]>(`registration-requests/${id}`)
        .then(self.normalizeNewUserRequestRegistration)
        .catch((error) => {
          console.error('Flow: API Error:', error);
          throw new Error(error);
        });
      return result;
    }),
    acceptChange: flow(function* (id: number, data: any) {
      const result = yield axios
        .post(`profile-requests/${id}/accept`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    viewedEndRequest: flow(function* (id: number) {
      const result = yield axios
        .post(`profile-requests/${id}/read`)
        .then(() => console.log('Data has been mutated', { id }))
        .catch((error) => {
          throw new Error(error);
        });

      return result;
    }),
    acceptAllChange: flow(function* (id) {
      const result = yield axios
        .post(`profile-requests/${id}/accept-all`)
        .then()
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    rejectChange: flow(function* (id: number, data: any) {
      const result = yield axios
        .post(`profile-requests/${id}/reject`, data)
        .then()
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    rejectAllChange: flow(function* (id, data: any) {
      const result = yield axios
        .post(`profile-requests/${id}/reject-all`, data)
        .then()
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getUserChangeFormDetail: flow(function* (id: number) {
      const result = yield axios
        .get<IUserChangeFormDetailModel[]>(`admin/edit-user-profile-change-detail/${id}`)
        .then(self.normalizeUserFormDetailRequest)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateUserEditRequest: flow(function* (data: IUserEditRequestModel) {
      const result = yield axios
        .put(`admin/user-profile-change-detail/${self.selectedId}`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateUserFormEditRequest: flow(function* (id: number, data: IUserChangeFormDetailModel) {
      const result = yield axios
        .post(`profile-requests/${id}/accept`, data)
        .then()
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateApproveUser: flow(function* (id: number, data: any) {
      yield axios
        .post(`registration-requests/${id}/approve-edit`, data)
        .then((res) => console.log(res))
        .catch((error) => {
          throw new Error(error);
        });
    }),
    rejectNewUserApi: flow(function* (id: number, data: any) {
      yield axios
        .post(`registration-requests/${id}/reject`, data)
        .then((res) => console.log(res))
        .catch((error) => {
          throw error.response.data.data.message;
        });
    }),
    approveNewUserRequest: flow(function* (id: number) {
      yield axios
        .post(`registration-requests/${id}/approve`)
        .then((res) => console.log(res))
        .catch((error) => {
          throw error.response.data.data.message;
        });
    }),

    newUserRequestReadNotification: flow(function* (id: number) {
      yield axios
        .post(`registration-requests/${id}/read`)
        .then((res) => console.log(res))
        .catch((error) => {
          throw error.response.data.data.message;
        });
    }),
    NotificationCounter: flow(function* () {
      const result = yield axios
        .get(`profile-requests/all-count`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data.data.message;
        });
      return result;
    }),
  }));

export type IUserEditRequestTableStore = Instance<typeof UserEditRequestTableStore>;
